import { calculateCroppedDimensions } from '@/utils/aspectRatioUtils';

import { type LoaderProps } from '@/components/Loader';
import type { ProductTypeOptions } from '@/lib/product';

import { NextImage } from '../NextImage';

import * as S from './AspectRatioImage.styles';
export interface AspectRatioImageProps {
  alt: string;
  aspectRatio: { x: number; y: number };
  className?: string;
  focalPoint: { x: number; y: number };
  link?: string;
  loaderProps?: LoaderProps;
  loading: 'eager' | 'lazy' | undefined;
  mediaWrapperType?: string;
  originalImageDimensions?: { x: number; y: number };
  productType?: ProductTypeOptions;
  scale?: number;
  setLoadingState?: (state: string) => void;
  showFallbackPlaceholder?: boolean;
  showPlaceholderBackground?: boolean;
  sizes: string;
  src: string;
  useLoader: boolean;
  width: string;
  widths?: number[];
}

export const AspectRatioImage: React.FC<AspectRatioImageProps> = (props) => {
  const {
    alt,
    aspectRatio,
    className,
    focalPoint,
    link,
    loading,
    mediaWrapperType,
    originalImageDimensions,
    scale,
    setLoadingState,
    showFallbackPlaceholder,
    showPlaceholderBackground,
    sizes,
    src,
    width,
  } = props;
  const { croppedHeight, croppedWidth } = calculateCroppedDimensions(
    {
      height: originalImageDimensions?.y ?? 0,
      width: originalImageDimensions?.x ?? 0,
    },
    aspectRatio?.x ?? 0,
    aspectRatio?.y ?? 0,
  );
  const handleImageStateChange = (state: 'loading' | 'error' | 'loaded') => {
    setLoadingState?.(state);
  };
  return (
    <S.ImageContainer
      $aspectRatio={
        aspectRatio ? `${aspectRatio?.x} / ${aspectRatio?.y}` : null
      }
      $hasUrl={!!link}
      $mediaWrapperType={mediaWrapperType}
      $scale={scale}
      $width={width}
      className={`aspect-ratio-image ${className}`}
      data-testid='AspectRatioImageImageContainer'
      onClick={() => {
        if (link) window.location.href = link;
      }}
    >
      <NextImage
        alt={alt}
        aspectRatioHeight={aspectRatio.y ?? 0}
        aspectRatioWidth={aspectRatio.x ?? 0}
        className={`aspect-ratio-image__img ${className}`}
        data-test-id='AspectRatioImageStyledImage'
        focalPoint={{ x: focalPoint.x, y: focalPoint.y }}
        height={croppedHeight}
        loading={loading}
        onStateChange={handleImageStateChange}
        originalImageDimensions={originalImageDimensions}
        priority={loading !== 'lazy'}
        showFallbackPlaceholder={showFallbackPlaceholder}
        showPlaceholderBackground={showPlaceholderBackground}
        sizes={sizes}
        src={src ?? ''}
        width={croppedWidth}
      />
    </S.ImageContainer>
  );
};

AspectRatioImage.displayName = 'AspectRatioImage';
